// apps/client/src/lib/api.ts
//
// API client for communicating with the backend
// Extended with missions, referrals, and ads endpoints
//

// @ts-ignore
const API_URL = import.meta.env.VITE_API_URL;

// === TYPES ===
export interface Mission {
    id: string;
    name: string;
    description: string;
    icon: string | null;
    type: 'DAILY' | 'WEEKLY' | 'PERMANENT';
    action: 'DAILY_LOGIN' | 'WATCH_AD' | 'PLAY_GAME' | 'WIN_LINE' | 'WIN_TWO_LINES' | 'WIN_BINGO' | 'INVITE_FRIEND';
    targetCount: number;
    rewardChips: number;
    currentCount: number;
    status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CLAIMED';
    canClaim: boolean;
}

export interface ReferralStats {
    totalReferrals: number;
    pendingReferrals: number;
    completedReferrals: number;
    totalEarned: number;
    referrals: Array<{
        id: string;
        referredUsername: string;
        status: 'PENDING' | 'COMPLETED' | 'REWARDED';
        reward: number;
        createdAt: string;
    }>;
}

export interface ReferralCode {
    code: string;
    totalUses: number;
    successfulUses: number;
    totalEarned: number;
    shareUrl: string;
}

export interface WheelPrize {
    id: string;
    name: string;
    type: 'CHIPS' | 'TON' | 'TICKETS' | 'NOTHING';
    amount: number;
}

export interface WheelStatusResponse {
    ok: boolean;
    hasFreeSpin: boolean;
    nextFreeSpinAt: string | null;
}

export interface WheelSpinResponse {
    ok: boolean;
    prize: WheelPrize;
    isFreeSpin: boolean;
}

class ApiClient {
    private token: string | null = null;

    setToken(token: string) {
        this.token = token;
    }

    clearToken() {
        this.token = null;
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (this.token) {
            (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
        }

        const response = await fetch(`${API_URL}${endpoint}`, {
            ...options,
            headers,
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `HTTP ${response.status}`);
        }

        return response.json();
    }

    // ============ GENERIC HELPERS ============
    async get<T = any>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, { method: 'GET' });
    }

    async post<T = any>(endpoint: string, body?: any): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: body != null ? JSON.stringify(body) : null,
        });
    }

    // ============ STORE ============
    async getStoreItems() {
        return this.get<{ ok: boolean; items: any[] }>('/store/items');
    }

    async buyStoreItem(itemId: string) {
        return this.post<{ ok: boolean; purchased: any; newBalance: number }>('/store/buy', { itemId });
    }

    async getMyPowerUps() {
        return this.get<{ ok: boolean; powerups: any[] }>('/store/my-powerups');
    }

    // ============ VOTING ============
    async getVotingOptions() {
        return this.get<{
            ok: boolean;
            options: Array<{ id: string; name: string; pattern: boolean[][] }>;
            userVote: string | null;
        }>('/voting/options');
    }

    async castVote(optionId: string) {
        return this.post<{ ok: boolean; message: string; voteId?: string }>('/voting/cast', { optionId });
    }

    // ============ SUNDAY TICKET ============
    async buySundayTicket() {
        return this.post<{ ok: boolean; ticket: any; newBalance: number }>('/game/sunday-ticket');
    }

    async getSundayInfo() {
        return this.get<{
            ok: boolean;
            ticketPrice: number;
            userTicketCount: number;
            userTickets: Array<{
                id: string;
                grid: (number | null)[][];
                hits: number[];
                status: string;
                createdAt: string;
            }>;
            sessionStatus: string | null;
            sessionId: string | null;
            startTime: string | null;
        }>('/game/sunday-info');
    }

    // ============ AUTH ============
    async login(initData: string) {
        return this.request<{
            ok: boolean;
            accessToken: string;
            user: {
                id: string;
                telegramId: string;
                username: string | null;
                firstName: string;
                chips: number;
            };
        }>('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ initData }),
        });
    }

    // ============ GAME ============
    async getCurrentSession() {
        return this.request<{
            ok: boolean;
            session: {
                id: string;
                type: string;
                status: string;
                playerCount: number;
                drawnNumbers: number[];
                createdAt: string;
            };
        }>('/game/session/current');
    }

    async getGameState() {
        return this.request<{
            ok: boolean;
            state: {
                sessionId: string | null;
                status: string;
                type: string | null;
                timeUntilStart: number | null;
                timeUntilStartFormatted: string | null;
                ballsDrawn: number;
                totalBalls: number;
                drawnNumbers: number[];
                playerCount: number;
                lineAwarded: boolean;
                twoLinesAwarded: boolean;
                sundayPrizePool: number;
                dailyLinePrize: number;
                dailyTwoLinesPrize: number;
                dailyBingoPrize: number;
            };
        }>('/game/state');
    }

    async buyTicket(gameType: string = 'DAILY_15', payWithChips: boolean = false) {
        return this.request<{
            ok: boolean;
            ticket: {
                id: string;
                sessionId: string;
                grid: (number | null)[][];
                status: string;
                createdAt: string;
            };
        }>('/game/ticket', {
            method: 'POST',
            body: JSON.stringify({ gameType, payWithChips }),
        });
    }

    async getMyTickets() {
        return this.request<{
            ok: boolean;
            tickets: Array<{
                id: string;
                sessionId: string;
                grid: (number | null)[][];
                hits: number[];
                status: string;
                createdAt: string;
                gameType: string | null;
            }>;
        }>('/game/tickets');
    }

    // ============ USER ============
    async getMe() {
        return this.request<{
            ok: boolean;
            user: {
                id: string;
                telegramId: string;
                username: string | null;
                firstName: string;
                lastName: string | null;
                chips: number;
                photoUrl: string | null;
                isPremium: boolean;
                hasCompletedOnboarding: boolean;
                hasAcceptedLegal: boolean;
                hasClaimedWelcomeBonus: boolean;
            };
        }>('/users/me');
    }

    async completeOnboarding() {
        return this.post<{ ok: boolean; hasCompletedOnboarding: boolean }>('/users/complete-onboarding');
    }

    async claimWelcomeBonus() {
        return this.post<{ ok: boolean; reward: number; newBalance: number }>('/users/claim-welcome-bonus');
    }

    async acceptLegal() {
        return this.post<{ ok: boolean; hasAcceptedLegal: boolean }>('/users/accept-legal');
    }

    // ============ MISSIONS ============
    async getMissions() {
        return this.request<{
            ok: boolean;
            missions: Mission[];
        }>('/missions');
    }

    async claimMission(missionId: string) {
        return this.request<{
            ok: boolean;
            reward: number;
            newBalance: number;
        }>(`/missions/${missionId}/claim`, {
            method: 'POST',
        });
    }

    async triggerAdWatched() {
        return this.request<{
            ok: boolean;
            message: string;
        }>('/missions/trigger/ad', {
            method: 'POST',
        });
    }

    // ============ REFERRALS ============
    async getReferralCode() {
        return this.request<{
            ok: boolean;
            code: string;
            totalUses: number;
            successfulUses: number;
            totalEarned: number;
            shareUrl: string;
        }>('/referral/code');
    }

    async applyReferralCode(code: string) {
        return this.request<{
            ok: boolean;
            message: string;
        }>('/referral/apply', {
            method: 'POST',
            body: JSON.stringify({ code }),
        });
    }

    async getReferralStats() {
        return this.request<{
            ok: boolean;
            stats: ReferralStats;
        }>('/referral/stats');
    }

    async getReferralLeaderboard(limit: number = 20) {
        return this.request<{
            ok: boolean;
            leaderboard: Array<{
                userId: string;
                username: string;
                successfulReferrals: number;
                totalEarned: number;
            }>;
        }>(`/referral/leaderboard?limit=${limit}`);
    }

    // ============ ADS ============
    // Ad rewards are claimed client-side after Adsgram show() resolves.
    // POST /ads/claim → server credits chips with rate limiting.

    async claimAdReward(placement: string, adToken?: string) {
        return this.request<{
            ok: boolean;
            reward: number;
            newBalance: number;
        }>('/ads/claim', {
            method: 'POST',
            body: JSON.stringify({ placement, adToken }),
        });
    }

    /**
     * Get a one-time ad verification token.
     * Must be called BEFORE showing the ad.
     */
    async getAdToken(placement: string) {
        return this.request<{ ok: boolean; adToken: string; expiresIn: number }>(
            `/ads/token?placement=${encodeURIComponent(placement)}`,
        );
    }

    /**
     * Track a card-only ad view (game_single, game_bundle) server-side.
     * No chip reward — creates an AdTracking record for analytics + bundle validation.
     */
    async trackAdView(placement: string) {
        return this.request<{ ok: boolean }>('/ads/trigger-watched', {
            method: 'POST',
            body: JSON.stringify({ placement }),
        });
    }

    /**
     * Validate and claim a 5-card bundle after watching 3 game_bundle ads.
     * Server verifies 3 recent game_bundle ads exist for this user.
     */
    async claimBundle() {
        return this.request<{ ok: boolean; qualifyingAds: number }>('/ads/claim-bundle', {
            method: 'POST',
        });
    }

    // ============ ECONOMY ============
    async getPublicSettings() {
        return this.request<{
            ok: boolean;
            data: {
                sundayTicketPrice: number;
                dailyBingoReward: number;
                dailyLoginReward: number;
                dailyMissionReward: number;
                walletCap: number;
                welcomeBonus: number;
            };
        }>('/settings/public');
    }

    // ============ WALLET ============
    async updateWallet(walletAddress: string) {
        return this.request<{ ok: boolean; walletAddress: string }>('/users/wallet', {
            method: 'PUT',
            body: JSON.stringify({ walletAddress }),
        });
    }

    // ============ ADMIN: MISSIONS ============
    async getAdminMissions() {
        return this.request<{
            ok: boolean;
            missions: Array<{
                id: string;
                name: string;
                description: string;
                icon: string | null;
                type: string;
                action: string;
                targetCount: number;
                rewardChips: number;
                isActive: boolean;
                sortOrder: number;
                createdAt: string;
                updatedAt: string;
            }>;
        }>('/admin/missions');
    }

    async createMission(data: {
        name: string;
        description: string;
        icon?: string;
        type: string;
        action: string;
        targetCount: number;
        rewardChips: number;
        isActive?: boolean;
        sortOrder?: number;
    }) {
        return this.request<{ ok: boolean; mission: Record<string, unknown> }>('/admin/missions', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateMission(id: string, data: Record<string, unknown>) {
        return this.request<{ ok: boolean; mission: Record<string, unknown> }>(`/admin/missions/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteMission(id: string) {
        return this.request<{ ok: boolean; message: string }>(`/admin/missions/${id}`, {
            method: 'DELETE',
        });
    }

    async toggleMission(id: string) {
        return this.request<{ ok: boolean; mission: Record<string, unknown> }>(`/admin/missions/${id}/toggle`, {
            method: 'POST',
        });
    }

    async reorderMissions(order: { id: string; sortOrder: number }[]) {
        return this.request<{ ok: boolean; message: string }>('/admin/missions/reorder', {
            method: 'PUT',
            body: JSON.stringify({ order }),
        });
    }

    // ============ ADMIN: USERS ============
    async getAdminUsers(page = 1, limit = 50, search?: string) {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (search) params.set('search', search);
        return this.request<{
            ok: boolean;
            users: Array<{
                id: string;
                telegramId: string;
                username: string | null;
                firstName: string | null;
                lastName: string | null;
                displayName: string;
                chips: number;
                walletAddress: string | null;
                isPremium: boolean;
                isAdmin: boolean;
                isBanned: boolean;
                createdAt: string;
                lastLoginAt: string | null;
            }>;
            pagination: { page: number; limit: number; total: number; totalPages: number };
        }>(`/admin/users?${params.toString()}`);
    }

    async toggleUserBan(userId: string, isBanned: boolean) {
        return this.request<{ ok: boolean; message: string }>(
            '/admin/users/ban',
            { method: 'POST', body: JSON.stringify({ userId, isBanned }) }
        );
    }

    // ============ ADMIN: GAMES ============
    async getAdminGames(page = 1, limit = 20, type?: string, status?: string) {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (type) params.set('type', type);
        if (status) params.set('status', status);
        return this.request<{
            ok: boolean;
            sessions: Array<{
                id: string;
                type: string;
                status: string;
                playerCount: number;
                drawnNumbers: number;
                jackpotSnapshot: number;
                startTime: string | null;
                endTime: string | null;
                createdAt: string;
            }>;
            pagination: { page: number; limit: number; total: number; totalPages: number };
        }>(`/admin/games/sessions?${params.toString()}`);
    }

    async getAdminWinners(page = 1, limit = 20) {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        return this.request<{
            ok: boolean;
            winners: Array<{
                ticketId: string;
                winType: string;
                userId: string;
                username: string | null;
                firstName: string | null;
                displayName: string;
                telegramId: string | null;
                walletAddress: string | null;
                sessionId: string;
                sessionType: string | null;
                sessionEndTime: string | null;
                createdAt: string;
            }>;
            pagination: { page: number; limit: number; total: number; totalPages: number };
        }>(`/admin/games/winners?${params.toString()}`);
    }

    // ============ ADMIN FIGURES ============
    async getAdminFigures() {
        return this.get<{ ok: boolean; figures: any[] }>('/admin/games/figures');
    }

    async createAdminFigure(data: { name: string; pattern: boolean[][]; isActive?: boolean }) {
        return this.post<{ ok: boolean; figure: any }>('/admin/games/figures', data);
    }

    async updateAdminFigure(id: string, data: any) {
        return this.request<{ ok: boolean; figure: any }>(`/admin/games/figures/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
    }

    async deleteAdminFigure(id: string) {
        return this.request<{ ok: boolean }>(`/admin/games/figures/${id}`, { method: 'DELETE' });
    }

    // ============ ADMIN TOURNAMENTS ============
    async getAdminTournaments() {
        return this.get<{
            ok: boolean;
            tournaments: Array<{
                id: string;
                title: string;
                startTime: string;
                prizePool: number;
                ticketPrice: number;
                maxBalls: number;
                cardFormat: string;
                status: string;
                gameSessionId: string | null;
                figureId: string | null;
                figureName: string | null;
                ticketCount: number;
                createdAt: string;
            }>;
        }>('/admin/games/tournaments');
    }

    async getTournamentBuyers(id: string) {
        return this.get<{
            ok: boolean;
            buyers: Array<{
                userId: string;
                telegramId: string;
                username: string | null;
                displayName: string;
                ticketCount: number;
            }>;
            totalTickets: number;
            totalBuyers: number;
        }>(`/admin/games/tournaments/${id}/tickets`);
    }

    async createTournament(data: {
        title: string;
        startTime: string;
        prizePool?: number;
        ticketPrice?: number;
        maxBalls?: number;
        cardFormat?: string;
        figureId?: string;
    }) {
        return this.post<{ ok: boolean; tournament: any }>('/admin/games/tournaments', data);
    }

    async updateTournament(id: string, data: any) {
        return this.request<{ ok: boolean; tournament: any }>(`/admin/games/tournaments/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async deleteTournament(id: string) {
        return this.request<{ ok: boolean; message: string }>(`/admin/games/tournaments/${id}`, {
            method: 'DELETE',
        });
    }

    // ============ PUBLIC TOURNAMENTS ============
    async getTournaments() {
        return this.get<{
            ok: boolean;
            tournaments: Array<{
                id: string;
                title: string;
                startTime: string;
                prizePool: number;
                prizeUnit: string;
                ticketPrice: number;
                maxBalls: number;
                cardFormat: string;
                status: string;
                gameSessionId: string | null;
                figure: { id: string; name: string; pattern: boolean[][] } | null;
                userTicketCount: number;
                userTickets: Array<{
                    id: string;
                    grid: (number | null)[][];
                    hits: number[];
                    status: string;
                    createdAt: string;
                }>;
                totalTicketsSold: number;
            }>;
        }>('/game/tournaments');
    }

    async buyTournamentTicket(tournamentId: string) {
        return this.post<{
            ok: boolean;
            ticket: {
                id: string;
                sessionId: string;
                grid: (number | null)[][];
                status: string;
                createdAt: string;
            };
            newBalance: number;
            tournamentId: string;
        }>('/game/tournament-ticket', { tournamentId });
    }

    // ============ PRIZES ============

    async getPendingPrizes() {
        return this.request<{
            ok: boolean;
            prizes: Array<{
                id: string;
                type: string;
                amount: number;
                description: string | null;
                createdAt: string;
            }>;
        }>('/game/pending-prizes');
    }

    // ============ FORTUNE WHEEL ============
    async getWheelStatus() {
        return this.request<WheelStatusResponse>('/wheel/status');
    }

    async spinWheel(adToken?: string) {
        return this.post<WheelSpinResponse>('/wheel/spin', adToken ? { adToken } : {});
    }
}

export const api = new ApiClient();
